import { useStores } from '@monorepo/controlled/src/hooks/use-stores';
import { observer } from 'mobx-react';
import { IAdminxStore } from '../../../../modules/stores';
import { BottomBar, BottomBarActions } from '@monorepo/base/src/components/bottom-bar/bottom-bar';
import { PrimaryButton } from '@monorepo/base/src/components/buttons/buttons';
import { Disable } from '@monorepo/base/src/components/disable/disable';
import { Page, Pagebar, PagebarTitle } from '@monorepo/base/src/components/page/page';
import { DataAttribute } from '@monorepo/tools/src/lib/models/data-attr.model';
import styles from './create-bidder.module.scss';
import { ErrorMsg } from '../../../../modules/components/error-msg/error-msg';
import { BidderCampaignForm } from '../../../../modules/components/forms/bidder-campaign/bidder-campaign.form';
import { DispatchLogsTypes, useLogs } from '@monorepo/controlled/src/hooks/use-logs';
import { useNavigateParams } from '@monorepo/tools/src/lib/hooks/tools/use-navigate-params';
import { Fragment, useEffect, useState } from 'react';
import routes from '../../../../modules/routes/index.routes';
import { BidderApi } from '../../../../modules/apis/bidder.api';
import { ActionModal, ModalTypes } from '@monorepo/controlled/src/components/modal/modals/action-modal/action-modal';
import { useBidderCampaignTableOfContents } from '../../../../modules/hooks/toc/bidder-campaign.toc';
import { Nav, NavGroup, NavItem, NavPage } from '@monorepo/base/src/components/nav/nav';
import { TableOfContents } from '@monorepo/base/src/components/table-of-contents/table-of-contents';
import { useRoute } from '@monorepo/tools/src/lib/hooks/tools/use-route';

export const CreateBidderCampaign = observer(() => {
	const { bidderStore, modalsStore } = useStores<IAdminxStore>();
	const [isSaving, setIsSaving] = useState(false);
	const bidderCampaignCrudStore = bidderStore.getCrud();
	const isLoading = bidderCampaignCrudStore.getIsLoading();
	const isSuccess = bidderCampaignCrudStore.getIsSuccess();
	const error = bidderCampaignCrudStore.getHttpError();
	const campaign = bidderCampaignCrudStore.getData().campaign;
	const navigate = useNavigateParams();
	const { dispatchLog } = useLogs();
	const { tableOfContents, isTableOfContents, setIsTableOfContents } = useBidderCampaignTableOfContents();
	const { currentRoute } = useRoute();
	const [activeRoute, setActiveRoute] = useState<string>(currentRoute);

	useEffect(() => {
		setIsTableOfContents(true);

		return () => {
			bidderCampaignCrudStore.reset();
		};
	}, []);

	useEffect(() => {
		if (isSuccess) {
			navigate(routes.bidder.campaigns());
			dispatchLog({
				msg: `Campaign ${name} was successfully edited`,
				type: DispatchLogsTypes.Success,
			});
			bidderCampaignCrudStore.setIsSuccess(false);
		}
	}, [isSuccess]);

	const onCreateCampaign = () => {
		if (!bidderCampaignCrudStore.isValid()) {
			dispatchLog({
				msg: 'Form is invalid',
				type: DispatchLogsTypes.Error,
			});

			return;
		}

		setIsSaving(true);

		BidderApi.createCampaign(bidderCampaignCrudStore.getCreateFormData().toObject())
			.then(() => {
				navigate(routes.bidder.campaigns());
				dispatchLog({
					msg: `Campaign ${campaign?.campaign_name} was successfully created`,
					type: DispatchLogsTypes.Success,
				});
			})
			.catch((e: { data: { success: boolean; errors: { message: string; field: string }[] } }) => {
				modalsStore.addModal({
					children: (
						<ActionModal type={ModalTypes.Error} primaryBtnTitle={'OK'}>
							<div className={styles.errorWrapper}>
								<h2 className={styles.title}>{`Failed to create campaign.`}</h2>
								{e.data.errors.map(error => (
									<div className={styles.errorContent}>{`${error.field}: ${error.message}`}</div>
								))}
							</div>
						</ActionModal>
					),
				});
			})
			.finally(() => {
				setIsSaving(false);
			});
	};

	return (
		<NavPage>
			<Nav onNavClick={({ to }) => to && setActiveRoute(to)} activeRoute={activeRoute}>
				<NavItem to={`${routes.bidder.campaigns()}`}>Campaigns</NavItem>
				<Fragment>
					{isTableOfContents && (
						<NavGroup title={'Table of contents'}>
							<TableOfContents heads={tableOfContents} />
						</NavGroup>
					)}
				</Fragment>
			</Nav>
			<Page unstyled={true}>
				<Disable is={isLoading || isSaving}>
					<Page>
						<Pagebar>
							<PagebarTitle unstyled={true}>
								<div className={styles.title}>Create a campaign</div>
							</PagebarTitle>
						</Pagebar>
						<BidderCampaignForm />
					</Page>
					<BottomBar>
						{error ? <ErrorMsg error={error} /> : null}
						<BottomBarActions>
							<PrimaryButton
								onClick={() => onCreateCampaign()}
								isRotateIcon={isLoading}
								icon={isLoading ? 'loading-01' : 'send-03'}
								debugProps={{ dataAttrs: [new DataAttribute('id', 'create_campaign_button')] }}>
								Create
							</PrimaryButton>
						</BottomBarActions>
					</BottomBar>
				</Disable>
			</Page>
		</NavPage>
	);
});
